import React, { forwardRef, Ref, useCallback, useMemo, useRef } from 'react';
import { Components, ExtendedComponentSchema } from 'formiojs';
import {
  EhsFormioReactComponent,
  EhsFormioReactProps,
  FormioBuilderInfo,
  FormioBuilderSchema
} from '../';
import { Breakpoints, intl, useBreakpoint } from '../../../../lib';
import { LanguageProperty } from '../../../generated/LanguageProperty';
import { Component } from '@formio/react';
import { HBox } from '../../../layout';
import { Tool } from '../../../basic';
import { Arrow, useToggleLayer } from 'react-laag';
import { DEFAULT_PLACEMENT_CONFIG, mergeRefs } from '../../../../util';

//START-EHS-REACT-FORMIO-DEFINITION---------------------------------------------
export type EhsFormioInfoModalConstructor = {
  heading: LanguageProperty;
  modalTitle: LanguageProperty;
  modalContent: LanguageProperty;
  modalList: string | null;
  orderedList: boolean;
};

/**
 *
 * @param heading The title that is shown beside the Info Icon.
 * @param modalTitle The (optional) title that is shown when the modal opens.
 * @param modalContent The (optional) content that is shown inside the modal.
 * @param modalList The (optional) list that is shown underneath the content inside the modal.
 * @param orderedList Defines whether the list should be <ol> or <ul>.
 */
export type EhsFormioInfoModalSettings = {
  heading: LanguageProperty;
  modalTitle?: LanguageProperty;
  modalContent?: LanguageProperty | JSX.Element;
  modalList?: Array<LanguageProperty>;
  orderedList: boolean;
};
export const EhsFormioInfoModalType = 'ehsinfomodal';
export class EhsFormioInfoModal extends EhsFormioReactComponent<
  EhsFormioInfoModalSettings,
  EhsFormioInfoModalConstructor
> {
  /**
   * defines the default schema of the custom component
   * https://help.form.io/developers/custom-components#component-methods
   */
  static schema(
    extend?: FormioBuilderSchema
  ): ExtendedComponentSchema<FormioBuilderSchema> {
    return Components.components.field.schema({
      type: EhsFormioInfoModalType,
      key: EhsFormioInfoModalType,
      heading: 'common.unknown',
      modalTitle: 'common.unknown',
      modalContent: 'common.unknown',
      modalList: 'common.unknown\ncommon.unknown',
      orderedList: false,
      ...extend
    });
  }
  /**
   * defines how the component will look in the Form Editor (Builder)
   * https://help.form.io/developers/custom-components#component-methods
   */
  static get builderInfo(): FormioBuilderInfo {
    return {
      title: 'Ehs-InfoModal',
      group: 'advanced',
      icon: 'file',
      documentation: '/developers/custom-components',
      weight: 40,
      schema: EhsFormioInfoModal.schema()
    };
  }

  /**
   * defines the fields available in the Editor/Builder
   * https://help.form.io/developers/form-builder#overriding-behavior
   */
  static editForm() {
    return {
      components: [
        {
          type: 'textfield',
          key: 'heading',
          label: 'Heading',
          tooltip: 'Please add only valid translation keys'
        },
        {
          type: 'textfield',
          key: 'modalTitle',
          label: 'Title of the Modal',
          tooltip: 'Please add only valid translation keys (mandatory field)',
          weight: 20
        },
        {
          type: 'textfield',
          key: 'modalContent',
          label: 'Content of the Modal',
          tooltip: 'Please add only valid translation keys',
          weight: 30
        },
        {
          type: 'textarea',
          key: 'modalList',
          label: 'Optional List (shown below Content)',
          tooltip:
            'Please add only valid translation keys, each list item in a new line (separated by newline)',
          weight: 40
        },
        {
          type: 'checkbox',
          key: 'orderedList',
          label: 'Show as ordered list',
          tooltip:
            'Set to true if you want to see the list items as ordered list(1.2.3...), set to false for an unordered list (bullets)',
          weight: 50
        }
      ]
    };
  }

  //Begin Non static definitions-------------------------------------
  private heading: LanguageProperty = 'common.unknown';
  private modalTitle: LanguageProperty = 'common.unknown';
  private modalContent: LanguageProperty = 'common.unknown';
  private modalList: Array<LanguageProperty> = [
    'common.unknown',
    'common.unknown'
  ];
  private orderedList = false;

  extractFields(component: Component<EhsFormioInfoModalConstructor>): void {
    this.heading = component.heading;
    this.modalTitle = component.modalTitle;
    this.modalContent = component.modalContent;
    // modalList:
    // a workaround to show a list of language keys we get from the form builder as string separated by new lines
    const array = (component.modalList ?? '').split('\n');
    this.modalList = array.filter(
      value => value !== ''
    ) as Array<LanguageProperty>;
    this.orderedList = component.orderedList;
  }

  attachEhsReact(): [
    (props: EhsFormioReactProps<EhsFormioInfoModalSettings>) => JSX.Element,
    EhsFormioInfoModalSettings
  ] {
    return [
      InfoModal,
      {
        heading: this.heading,
        modalTitle: this.modalTitle,
        modalContent: this.modalContent,
        modalList: this.modalList,
        orderedList: this.orderedList
      }
    ];
  }
}
//END-EHS-REACT-FORMIO-DEFINITION-----------------------------------------------

/**
 * This might cause some memory to be allocated longer than it ideally should be.
 *
 * As soon as we update beyond typescript 4.3.* we can replace it with this.
 * https://developer.mozilla.org/en-US/docs/Web/JavaScript/Reference/Global_Objects/WeakRef
 */
let closeModalPrev: null | (() => void) = null;

export type InfoModalProps = Omit<
  EhsFormioReactProps<EhsFormioInfoModalSettings>,
  'value' | 'onChange' | 'label' | 'readOnly'
>;
export function InfoModal({ settings }: InfoModalProps): JSX.Element {
  const mobile = useBreakpoint(Breakpoints.MOBILE);
  const dialog = useRef<HTMLDialogElement>(null);
  const getList = useMemo((): JSX.Element => {
    if (settings.modalList && settings.modalList.length > 0) {
      const getItems = () => {
        return (settings.modalList ?? []).map(item => (
          <li key={item}>{intl(item)}</li>
        ));
      };
      if (settings.orderedList) {
        return <ol>{getItems()}</ol>;
      } else {
        return <ul>{getItems()}</ul>;
      }
    } else {
      return <></>;
    }
  }, [settings.modalList, settings.orderedList]);

  const modalTitle = useMemo(() => {
    return settings.modalTitle ? (
      <h4 className="ehs-modalTitleH4">{intl(settings.modalTitle)}</h4>
    ) : (
      <div className="ehs-modalTitleEmpty" />
    );
  }, [settings.modalTitle]);

  const [element, toggleLayerProps] = UseCreateToggleLayer({
    modalTitle: modalTitle,
    modalContent: settings.modalContent,
    list: getList
  });

  const close = useCallback(() => {
    toggleLayerProps.close();
  }, [toggleLayerProps]);

  const open = useCallback(
    (event: React.MouseEvent<never>) => {
      if (mobile) {
        dialog.current?.showModal();
        return;
      }
      if (closeModalPrev != null) {
        closeModalPrev();
      }
      closeModalPrev = toggleLayerProps.close;
      toggleLayerProps.openFromMouseEvent(event);
    },
    [mobile, toggleLayerProps]
  );

  return (
    <HBox>
      {!mobile ? (
        element
      ) : (
        <InfoModalDialogComponent
          modalTitle={modalTitle}
          modalContent={settings.modalContent}
          list={getList}
          ref={dialog}
        />
      )}
      {settings.heading && (
        <h3 className="ehs-infoModalHeading">{intl(settings.heading)}</h3>
      )}
      <div className="ehs-infoModalIconWrapper">
        <Tool
          onClick={toggleLayerProps.isOpen ? close : open}
          icon="shui_information"
          text=""
          aria-label={intl(settings.heading)}
        />
      </div>
    </HBox>
  );
}

type InfoModalComponentProps = {
  modalTitle: JSX.Element;
  modalContent?: LanguageProperty | JSX.Element;
  list: JSX.Element;
};
const InfoModalDialogComponent = forwardRef(function InfoModalDialogComponent(
  { modalTitle, modalContent, list }: InfoModalComponentProps,
  ref: Ref<HTMLDialogElement> | undefined
): JSX.Element {
  const dialog = useRef<HTMLDialogElement>(null);
  return (
    <dialog ref={mergeRefs([ref, dialog])} className="ehs-infoModalDialog">
      <HBox className="ehs-infoModalTitle">
        {modalTitle}
        <Tool
          icon={'materialicon-close'}
          onClick={() => dialog.current?.close()}
          text=""
          aria-label={intl('view.button.close')}
        />
      </HBox>
      <div className="ehs-modalContent">
        {modalContent && typeof modalContent == 'string' ? (
          <span>{intl(modalContent)}</span>
        ) : (
          modalContent
        )}
        {list}
      </div>
    </dialog>
  );
});

function UseCreateToggleLayer({
  modalTitle,
  modalContent,
  list
}: InfoModalComponentProps) {
  return useToggleLayer(
    ({ isOpen, layerProps, close, layerSide, arrowStyle }) => {
      return (
        isOpen && (
          <div
            className="ehs-infoModalWrappr"
            {...layerProps}
            style={{ ...layerProps.style }}
          >
            <div className="ehs-infoModal">
              <HBox className="ehs-infoModalTitle">
                {modalTitle}
                <Tool
                  icon={'materialicon-close'}
                  onClick={close}
                  text=""
                  aria-label={intl('view.button.close')}
                />
              </HBox>
              <div className="ehs-modalContent">
                {modalContent && typeof modalContent == 'string' ? (
                  <span>{intl(modalContent)}</span>
                ) : (
                  modalContent
                )}
                {list}
              </div>
            </div>
            <Arrow
              size={10}
              roundness={0}
              layerSide={layerSide}
              borderWidth={1}
              backgroundColor="var(--base-0)"
              borderColor="var(--base-1)"
              style={{
                ...arrowStyle
              }}
            />
          </div>
        )
      );
    },
    {
      placement: DEFAULT_PLACEMENT_CONFIG,
      closeOnOutsideClick: true
    }
  );
}
